import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Hand,
  Gauge,
  Users,
  Lock,
  MessageSquareLock,
  Apple,
  Play,
  ArrowRight,
  Sparkles,
  Flame,
} from "lucide-react";
import { api } from "@/lib/api";
import { BloqLogo, BloqStacked } from "@/components/brand/BloqLogo";
import { Button } from "@/components/ui/button";
import { WaitlistForm } from "@/components/lander/WaitlistForm";

const compact = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n));

// Social-proof badge: the most-shared sticker pack this week (public, unauth). Self-hides until the
// mobile `GET /stickers/top-shared` endpoint ships (see HANDOFF_share_analytics.md) — never blocks the page.
function TopSharedPack() {
  const [pack, setPack] = useState(null);
  useEffect(() => {
    let alive = true;
    api.topSharedPack().then((res) => { if (alive && res?.pack_id) setPack(res); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!pack) return null;
  return (
    <Link
      to={`/app/stickers/${pack.pack_id}?ref=share`}
      data-testid="top-shared-pack"
      className="mt-8 inline-flex max-w-full items-center gap-2.5 rounded-full border border-bloq-mint/30 bg-bloq-mint/[0.06] px-4 py-2 text-sm text-white transition-colors hover:border-bloq-mint/60"
    >
      <Flame className="h-4 w-4 shrink-0 text-bloq-mint" />
      <span className="font-semibold">Trending this week</span>
      <span className="text-bloq-muted">·</span>
      <span className="truncate">{pack.title}{pack.author?.handle ? ` by @${pack.author.handle}` : ""}</span>
      {typeof pack.shares === "number" && pack.shares > 0 && (
        <span className="shrink-0 rounded-full bg-white/8 px-2 py-0.5 text-[11px] text-bloq-mint">{compact(pack.shares)} shares</span>
      )}
    </Link>
  );
}

function StoreBadge({ icon: Icon, top, bottom, testid }) {
  return (
    <div
      className="flex cursor-default items-center gap-3 rounded-2xl border border-white/12 bg-bloq-s1 px-4 py-2.5 opacity-90 transition-opacity hover:opacity-100"
      data-testid={testid}
    >
      <Icon className="h-6 w-6 text-white" />
      <div className="text-left leading-tight">
        <div className="text-[10px] uppercase tracking-wider text-bloq-mint">{top}</div>
        <div className="text-sm font-semibold text-white">{bottom}</div>
      </div>
    </div>
  );
}

const FEATURES = [
  {
    icon: ShieldCheck,
    title: "BLOQ Shield",
    body: "Every link and message is screened. Scams, spam and fake support get blocked before they reach you.",
    accent: "text-bloq-mint",
    glow: "rgba(1,250,189,0.16)",
  },
  {
    icon: Hand,
    title: "No cold DMs",
    body: "Strangers can't slide into your inbox. They must request a connection — Shield screens every one.",
    accent: "text-bloq-blue",
    glow: "rgba(53,145,244,0.16)",
  },
  {
    icon: Gauge,
    title: "BLOQ Score",
    body: "Your portable trust passport across Web3 — built from identity, behavior, contribution and standing.",
    accent: "text-bloq-purple",
    glow: "rgba(139,92,246,0.16)",
  },
  {
    icon: Users,
    title: "Trust-gated BLOQs",
    body: "Communities that require a minimum BLOQ Score or verification to join. Verified rooms, real people.",
    accent: "text-bloq-mint",
    glow: "rgba(1,250,189,0.16)",
  },
  {
    icon: MessageSquareLock,
    title: "End-to-end encrypted",
    body: "Conversations are encrypted end-to-end. Your words stay between you and the people you trust.",
    accent: "text-bloq-blue",
    glow: "rgba(53,145,244,0.16)",
  },
  {
    icon: Lock,
    title: "You hold the keys",
    body: "Wallet proof is read-only — a signature that verifies you, never your keys. Coming soon.",
    accent: "text-bloq-purple",
    glow: "rgba(139,92,246,0.16)",
  },
];

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-bloq-bg text-white">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0 grain opacity-60" />
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[560px] -translate-x-1/2 rounded-full bg-bloq-mint/10 blur-[140px]" />
      <div className="pointer-events-none absolute top-1/3 -right-40 h-[480px] w-[480px] rounded-full bg-bloq-purple/12 blur-[150px]" />
      <div className="pointer-events-none absolute bottom-0 -left-40 h-[420px] w-[420px] rounded-full bg-bloq-blue/10 blur-[150px]" />

      {/* Nav */}
      <header className="relative z-20">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8">
          <BloqLogo size={30} textClass="text-lg" data-testid="nav-logo" />
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/privacy"
              className="hidden text-sm text-bloq-muted transition-colors hover:text-white sm:inline"
              data-testid="nav-privacy-link"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className="hidden text-sm text-bloq-muted transition-colors hover:text-white sm:inline"
              data-testid="nav-terms-link"
            >
              Terms
            </Link>
            <Link to="/login" data-testid="nav-signin-link">
              <Button
                variant="ghost"
                className="rounded-full text-bloq-mint hover:bg-bloq-mint/10 hover:text-bloq-mint"
              >
                Sign in
              </Button>
            </Link>
            <Link to="/signup" data-testid="nav-getstarted-link">
              <Button className="rounded-full bloq-gradient font-semibold text-bloq-bg hover:opacity-90">
                Get started
              </Button>
            </Link>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-5 pb-16 pt-10 text-center sm:pt-16">
        <div className="animate-fade-up">
          <BloqStacked size={260} />
        </div>
        <h1
          className="mt-8 max-w-2xl animate-fade-up text-3xl font-bold leading-tight tracking-tight sm:text-5xl"
          style={{ animationDelay: "140ms" }}
        >
          The trust-gated messenger
          <br className="hidden sm:block" /> for the Web3 world
        </h1>

        <div
          className="mt-9 grid w-full max-w-lg animate-fade-up gap-4 text-left"
          style={{ animationDelay: "220ms" }}
        >
          {[
            { icon: ShieldCheck, text: "BLOQ Shield blocks scams, spam & fake support", c: "text-bloq-mint" },
            { icon: Hand, text: "No cold DMs — strangers must request a connection", c: "text-bloq-blue" },
            { icon: Gauge, text: "Your BLOQ Score is your portable trust passport", c: "text-bloq-purple" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3.5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-bloq-s2">
                <f.icon className={`h-5 w-5 ${f.c}`} />
              </span>
              <span className="text-base text-white/85">{f.text}</span>
            </div>
          ))}
        </div>

        <div
          className="mt-10 flex w-full max-w-md animate-fade-up flex-col gap-3"
          style={{ animationDelay: "300ms" }}
        >
          <Link to="/signup" className="w-full" data-testid="hero-create-account-btn">
            <Button className="h-14 w-full rounded-full bloq-gradient text-base font-bold text-bloq-bg shadow-mint hover:opacity-90">
              Create account
              <ArrowRight className="ml-1 h-5 w-5" />
            </Button>
          </Link>
          <Link to="/login" className="w-full" data-testid="hero-signin-btn">
            <Button
              variant="outline"
              className="h-14 w-full rounded-full border-2 border-bloq-mint/70 bg-transparent text-base font-bold text-bloq-mint hover:bg-bloq-mint/10"
            >
              Sign in
            </Button>
          </Link>
        </div>

        {/* Store badges */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <StoreBadge icon={Apple} top="Coming soon" bottom="App Store" testid="badge-app-store" />
          <StoreBadge icon={Play} top="Coming soon" bottom="Google Play" testid="badge-google-play" />
        </div>

        <TopSharedPack />

        <p className="mt-8 text-sm text-bloq-muted">
          Secure conversations. Verified communities. Safer Web3.
        </p>
      </section>

      {/* Features bento */}
      <section className="relative z-10 mx-auto max-w-6xl px-5 py-16 sm:px-8">
        <div className="mb-10 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-bloq-mint">
          <Sparkles className="h-4 w-4" /> Why BLOQ Talk
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="group relative overflow-hidden rounded-3xl border border-white/8 bg-bloq-s1 p-7 transition-all duration-300 hover:border-white/16 hover:-translate-y-1"
              data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div
                className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
                style={{ background: f.glow }}
              />
              <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-bloq-s2">
                <f.icon className={`h-6 w-6 ${f.accent}`} />
              </span>
              <h3 className="mb-2 text-lg font-semibold">{f.title}</h3>
              <p className="text-sm leading-relaxed text-bloq-muted">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Waitlist */}
      <section className="relative z-10 mx-auto max-w-3xl px-5 py-16 sm:px-8">
        <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-b from-bloq-s2 to-bloq-s1 p-8 text-center sm:p-12">
          <div className="pointer-events-none absolute -top-24 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-bloq-blue/15 blur-[110px]" />
          <h2 className="relative text-2xl font-bold sm:text-3xl">Join the waitlist</h2>
          <p className="relative mx-auto mt-3 max-w-md text-sm text-bloq-muted">
            Be first to know when BLOQ Talk lands on your platform. No spam — that's kind of the whole point.
          </p>
          <div className="relative mx-auto mt-8 max-w-md">
            <WaitlistForm />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-5 py-8 sm:flex-row sm:px-8">
          <BloqLogo size={24} textClass="text-sm" />
          <div className="flex items-center gap-6 text-sm text-bloq-muted">
            <Link to="/privacy" className="transition-colors hover:text-white" data-testid="footer-privacy-link">
              Privacy
            </Link>
            <Link to="/terms" className="transition-colors hover:text-white" data-testid="footer-terms-link">
              Terms
            </Link>
            <Link to="/data-security" className="transition-colors hover:text-white" data-testid="footer-security-link">
              Data Security
            </Link>
            <Link to="/delete-account" className="transition-colors hover:text-white" data-testid="footer-delete-link">
              Delete Account
            </Link>
            <Link to="/login" className="transition-colors hover:text-white">
              Sign in
            </Link>
          </div>
          <p className="text-xs text-bloq-muted">© {new Date().getFullYear()} BLOQ Talk</p>
        </div>
      </footer>
    </div>
  );
}
